import { ContactShadows, Environment, Loader } from '@react-three/drei';
import { Stats } from '@react-three/drei';
import { Canvas, Vector3 } from '@react-three/fiber';
import { EffectComposer, N8AO } from '@react-three/postprocessing';
import { observer } from 'mobx-react';
import { ErrorBoundary } from 'react-error-boundary';
import * as THREE from 'three';

import { GroupState } from '../core/GroupState';
import { ObjectState } from '../core/ObjectState';
import { SceneState } from '../core/SceneState';
import Camera, { CameraInitialSetup } from './Camera';
import Group from './Group';

interface ThreejsSceneProps {
  state: SceneState;
  backgroundColor?: string;
  className?: string;
  freeze?: boolean;
  onObjectClick?: (object: ObjectState) => void;
  onNoneObjectClick?: () => void;
  showPerformance: boolean;
  cameraSetup?: CameraInitialSetup;
  enableAO?: boolean;
}

function ThreejsScene({
  backgroundColor,
  className,
  state,
  freeze,
  onObjectClick,
  onNoneObjectClick,
  showPerformance,
  cameraSetup,
  enableAO,
}: ThreejsSceneProps) {
  return (
    <>
      <ErrorBoundary fallback={null}>
        <Canvas
          className={className}
          frameloop={freeze ? 'demand' : 'always'}
          gl={{ toneMapping: THREE.ACESFilmicToneMapping, antialias: true }}
          shadows
          onPointerMissed={onNoneObjectClick}
        >
          {backgroundColor && <color attach="background" args={[backgroundColor]} />}
          <ambientLight intensity={1} />
          <directionalLight
            castShadow
            intensity={3.5}
            shadow-mapSize-width={1024}
            shadow-mapSize-height={1024}
            shadow-camera-left={-10}
            shadow-camera-right={10}
            shadow-camera-top={10}
            shadow-camera-bottom={-10}
            shadow-camera-near={0.1}
            shadow-camera-far={50}
            shadow-blurSamples={10}
            shadow-radius={3}
            position={[0, 10, 0]}
            shadow-bias={-0.0005}
            shadow-normalBias={0.05}
          />
          <directionalLight intensity={1.5} position={[-3, 7, 1]} />
          <directionalLight intensity={1.5} position={[1, 1.5, -7]} />
          <directionalLight intensity={1.5} position={[-1, 1.5, 7]} />
          <Camera mode={state.mode} setup={cameraSetup} />
          <Environment preset="warehouse" environmentIntensity={0.3} />
          {/* The key is used here to envorce the contact shaddow refresh when new layout is loaded */}
          <ContactShadows
            frames={1}
            opacity={1}
            blur={0.5}
            far={0.06}
            key={state.objects.map((x) => `${x.name}${x.selected}`).join()}
          />
          {state.objects.map((object, index) => {
            if (object instanceof GroupState) {
              return <Group state={object} onObjectClick={onObjectClick} key={`${object.name}-${index}`} />;
            }
          })}
          {/* Performance monitor - displayed temporary for performance tracking */}
          {showPerformance && <Stats />}
          <EffectComposer>{enableAO && <N8AO color="black" aoRadius={0.3} intensity={2} />}</EffectComposer>
        </Canvas>
        <Loader
          containerStyles={{ background: '#fff' }}
          innerStyles={{ background: '#fff' }}
          barStyles={{ background: '#174e42' }}
          dataStyles={{ color: '#174e42', fontFamily: 'inherit', fontSize: '1.4rem' }}
        />
      </ErrorBoundary>
    </>
  );
}

export default observer(ThreejsScene);
